import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { GALLERY } from "../data/content";

export const Gallery = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 md:py-32 bg-[#F7F1E6] scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          chapter="04"
          overline="Featured Gallery"
          title="A Glimpse of the Craft"
          description="A curated look at pieces from our gold, diamond, silver and bridal showcases. Photography is refreshed regularly — visit us to see the full collection."
          center
        />

        <div className="columns-2 md:columns-3 gap-4 [column-fill:_balance] -mt-4 md:-mt-8">
          {GALLERY.map((g, i) => (
            <Reveal key={i} delay={0.05 * (i % 3)} className="mb-4 break-inside-avoid">
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(g)}
                className="group relative block w-full overflow-hidden rounded-sm"
                aria-label={`View ${g.label}`}
              >
                <img
                  src={g.image}
                  alt={g.alt}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105 ${i % 3 === 0 ? "aspect-[3/4]" : i % 3 === 1 ? "aspect-square" : "aspect-[4/5]"}`}
                />
                <div className="absolute inset-0 bg-royal-deep/0 group-hover:bg-royal-deep/45 transition-colors duration-500 flex items-center justify-center" aria-hidden="true">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 w-11 h-11 border border-gold text-gold flex items-center justify-center rounded-full">
                    <Plus size={18} strokeWidth={1.5} />
                  </span>
                </div>
                <span className="absolute bottom-3 left-3 text-[11px] tracking-[0.2em] uppercase text-ivory/0 group-hover:text-ivory/90 transition-colors duration-500">
                  {g.label}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[90] bg-royal-deep/95 backdrop-blur-sm flex items-center justify-center p-5 md:p-12"
            onClick={() => setActive(null)}
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setActive(null)}
              aria-label="Close"
              className="absolute top-5 right-5 w-11 h-11 border border-ivory/30 text-ivory flex items-center justify-center rounded-full hover:bg-ivory hover:text-royal-deep transition-colors duration-300"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
            <motion.figure
              initial={{ scale: 0.94, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 10 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.image} alt={active.alt} className="w-full max-h-[75vh] object-contain rounded-sm" />
              <figcaption className="mt-4 text-center text-sm tracking-[0.2em] uppercase text-gold">{active.label}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
